// The wire protocol: every message name and payload shape that crosses the
// client/server boundary is defined here and nowhere else.

export const MessageType = {
  /** client → server: current movement intent. */
  input: "input",
  /** client → server: send a chat line. */
  chat: "chat",
  /** server → clients: a chat line (player or system) to display. */
  chatMessage: "chat-message",
  /**
   * server → one client: you walked through a door — reconnect to `to`.
   * The server has already persisted the character at the arrival pose in
   * the target room, so the normal join/restore path lands them there.
   */
  portal: "portal",
  /** client → server: board a nearby parked skiff or ship. */
  mount: "mount",
  /** client → server: step off the machine you're riding. Ships only set
   *  you down when they ARE down — the server refuses mid-air. */
  dismount: "dismount",
  /** client → server: route the ship's aether pressure (a PowerFocus). */
  route: "route",
  /** client → server: fire the coil gun along an aim direction (intent —
   *  the server owns the cooldown, the coil charge, and the hit). */
  fire: "fire",
  /** server → clients (in view): a bolt was fired — render the tracer.
   *  Carries endpoints only; damage is never on the wire. */
  bolt: "bolt",
  /** client → server: read the concentration grid around you (surface). */
  survey: "survey",
  /** server → one client: the aetherometer's answer. */
  surveyResult: "survey-result",
  /** client → server: seal the reading just taken into a tradeable object. */
  sealReading: "seal-reading",
  /** client → server: start a hand-sampling channel where you stand. */
  sample: "sample",
  /** server → one client: what the sample yielded (or why it didn't). */
  sampleResult: "sample-result",
  /** client → server: offer units of a resource to someone standing near. */
  tradeOffer: "trade-offer",
  /** server → the receiver: someone is holding something out to you. */
  tradeRequest: "trade-request",
  /** client → server: take it, or wave it away. */
  tradeRespond: "trade-respond",
  /** server → both sides: how the hand-off actually went. */
  tradeResult: "trade-result",
  /** client → server: take a flyer off the stack by the club door. */
  takeFlyer: "take-flyer",
  /** server → one client: what came of reaching for one. */
  flyerTaken: "flyer-taken",
  /** client → server: do something with a crafted thing you hold. */
  useArtifact: "use-artifact",
  /** server → one client: what came of it (the room hears the rest). */
  artifactUsed: "artifact-used",
  /**
   * client → server: read the claim you are standing at (a stake or a
   * structure). The answer is prose over system chat — the server owns
   * the wording, as everywhere.
   */
  homesteadRead: "homestead-read",
  /**
   * client → server (home interiors): pick up the piece of furniture at
   * hand, or set down the one you are carrying where you stand. Direct
   * manipulation — the body is the cursor; no coordinates cross the wire
   * from the client, only "the thing I'm reaching for".
   */
  furnishHandle: "furnish-handle",
  /** client → server: a defensive verb — strike, evade, brace, steady. */
  combat: "combat",
  /** server → one client: the wilds telling you something, in words. */
  threat: "threat",
  /** client → server: stand a lot on the offer board (at the board). */
  marketPost: "market-post",
  /** client → server: fill a standing lot, whole or not at all. */
  marketFill: "market-fill",
  /** client → server: take your own lot back down. */
  marketWithdraw: "market-withdraw",
  /** server → one client: how the board act went, in words. */
  marketResult: "market-result",
  /** client → server: post a job on the settlement board (at the board). */
  jobPost: "job-post",
  /** client → server: take an offered job (charter kinds bind the worker). */
  jobAccept: "job-accept",
  /** client → server: hand over the goods a supply job asked for. */
  jobDeliver: "job-deliver",
  /** client → server: take your own job back down (escrow refunds). */
  jobCancel: "job-cancel",
  /** server → one client: how the job act went, in words. */
  jobResult: "job-result",
} as const;

export type MessageTypeName = (typeof MessageType)[keyof typeof MessageType];

/**
 * Movement intent. The client never sends a position — it sends what the
 * player is trying to do, and the server integrates it. moveX/moveZ form a
 * direction vector in world space (already camera-relative), magnitude ≤ 1;
 * the server clamps anything larger. yaw is the facing direction in radians.
 */
export interface InputMessage {
  moveX: number;
  moveZ: number;
  yaw: number;
  /** Hold-to-sprint. Only honored on the surface; indoors keeps club pace. */
  sprint?: boolean;
  /** Toggled walk gait (surface only). Sprint wins when both are set. */
  walk?: boolean;
  /** Vertical intent while piloting a ship: −1 sink … +1 climb. */
  climb?: number;
  /** Boost held (ships; spends surge, the server gates it on reserve). */
  boost?: boolean;
}

export interface RouteMessage {
  /** Where to send the pressure — a PowerFocus word (ship.ts). */
  focus: string;
}

/** Aim direction for a coil shot, world space (the gun rides a gimbal —
 *  camera-aimed). Normalized server-side; junk vectors are refused. */
export interface FireMessage {
  dx: number;
  dy: number;
  dz: number;
}

/** A bolt for the renderer: from muzzle to impact (or to max range on a
 *  miss). `source` picks the tracer look; `hit` drives the impact flash. */
export interface BoltMessage {
  sx: number;
  sy: number;
  sz: number;
  tx: number;
  ty: number;
  tz: number;
  source: "coil" | "warden";
  hit: boolean;
}

export interface ChatSendMessage {
  text: string;
}

export interface ChatBroadcastMessage {
  /** Display name of the sender; empty string for system messages. */
  from: string;
  text: string;
  /** Server clock, ms since epoch. */
  ts: number;
  system: boolean;
}

export interface PortalMessage {
  /** Target room id (an AreaPlan id). */
  to: string;
}

export interface MountMessage {
  /** Which hull to board — a skiff or ship equipment id (must be parked
   *  and within its class's mount range). Field name kept for wire compat. */
  skiffId: string;
}

/** Survey grid: SURVEY_GRID_SIZE × SURVEY_GRID_SIZE cells this far apart. */
export const SURVEY_GRID_SIZE = 5;
export const SURVEY_GRID_STEP_M = 32;
/** Hand-sampling channel duration; hold still or it fails. */
export const SAMPLE_CHANNEL_MS = 4000;

export interface SurveyMessage {
  /** Which active named spawn the aetherometer is tuned to. */
  spawnId: string;
}

export interface SurveyResultMessage {
  spawnId: string;
  name: string;
  /** Row-major grid (north-west first), concentrations 0..1. */
  grid: number[];
  stepM: number;
  /** Offset of the richest cell in grid steps (dx east, dz south). */
  best: { dx: number; dz: number; value: number };
}

export interface SampleMessage {
  spawnId: string;
}

export type SampleFailReason =
  | "weak-ground"
  | "moved"
  | "expired"
  | "busy"
  | "mounted"
  | "hold-full";

/** Both parties must be this close to hand something over. */
export const TRADE_RANGE_M = 6;

/**
 * What can change hands. Units of a named resource, a sealed reading and a
 * crafted artifact are different objects but the same social act —
 * somebody holds something out and you decide whether to take it — so they
 * share one flow rather than each growing their own.
 */
export const TRADE_ITEM_KINDS = ["units", "reading", "route_reading", "artifact"] as const;
export type TradeItemKind = (typeof TRADE_ITEM_KINDS)[number];

export interface TradeOfferMessage {
  /** Session of the person you are holding it out to. */
  toSessionId: string;
  kind: TradeItemKind;
  /** For "units": which named spawn, and how many. */
  spawnId?: string;
  units?: number;
  /** For "reading": which sealed reading. */
  readingId?: string;
  /** For "artifact": which made thing. The maker's mark travels with it. */
  artifactId?: string;
}

export interface TradeRequestMessage {
  offerId: string;
  fromName: string;
  kind: TradeItemKind;
  /** Server-composed, already human — "30 × Prenevane (Verdant Copper)". */
  what: string;
}

export interface TradeRespondMessage {
  offerId: string;
  accept: boolean;
}

/** Stand a lot: give units of a named spawn, want units of a class. */
export interface MarketPostMessage {
  spawnId: string;
  giveUnits: number;
  wantClassId: string;
  wantUnits: number;
}

export interface MarketFillMessage {
  offerId: string;
  /** Which named spawn of the wanted class the filler pays with. */
  spawnId: string;
}

export interface MarketWithdrawMessage {
  offerId: string;
}

export interface MarketResultMessage {
  ok: boolean;
  /** Player-facing sentence; the server owns the wording. */
  message: string;
}

export interface JobPostMessage {
  kind: "supply" | "charter";
  /** supply: bring this many units of this class. */
  wantClassId?: string;
  wantUnits?: number;
  /** charter: carry the client to this landmark. */
  destinationLandmarkId?: string;
  /** The reward, escrowed out of the poster's hold at post time. */
  rewardSpawnId: string;
  rewardUnits: number;
}

export interface JobAcceptMessage {
  jobId: string;
}

export interface JobDeliverMessage {
  jobId: string;
  /** Which named spawn of the wanted class settles the delivery. */
  spawnId: string;
}

export interface JobCancelMessage {
  jobId: string;
}

export interface JobResultMessage {
  ok: boolean;
  /** Player-facing sentence; the server owns the wording. */
  message: string;
}

export interface TradeResultMessage {
  ok: boolean;
  /** Player-facing sentence; the server owns the wording. */
  message: string;
}

/**
 * How close you must be to the stack by the door to take one. Generous —
 * it is a pile of paper on a table, not a lock.
 */
export const FLYER_REACH_M = 3;

export interface FlyerTakenMessage {
  ok: boolean;
  /** Player-facing sentence; the server owns the wording. */
  message: string;
  /** Set when one was actually taken, so the Ledger can catch up. */
  artifactId?: string;
}

/**
 * Doing something with a made thing you hold. Drinking and playing are
 * room acts — the club hears them, which is the point — so they travel as
 * room messages rather than HTTP, gated on the LIVE position. Wearing
 * works anywhere.
 */
export type ArtifactVerb = "drink" | "play" | "wear" | "shed";

export interface UseArtifactMessage {
  artifactId: string;
  verb: ArtifactVerb;
}

export interface ArtifactUsedMessage {
  ok: boolean;
  /** Player-facing sentence; the server owns the wording. */
  message: string;
  /** Set on success, so the Ledger can catch up. */
  artifactId?: string;
}

export interface HomesteadReadMessage {
  /** Claim id (stake) or structure id — whichever you are standing at. */
  targetId: string;
}

export interface FurnishHandleMessage {
  /** Set to pick a piece up; omitted to set the carried one down. */
  placeableId?: string;
}

/**
 * A defensive verb. The client says what the player TRIED to do; the
 * server decides what happened — range, arc, timing, and effect are all
 * resolved on the tick, and the answer arrives as words and posture,
 * never as a damage number (there are none anywhere in the system).
 */
export interface CombatMessage {
  /** A CombatVerb word (creatures.ts). */
  verb: string;
  /**
   * For `brace` only: the stance is held, so the client reports the key
   * going down (true) and coming back up (false). The other verbs are
   * instants and ignore this field.
   */
  on?: boolean;
}

/**
 * The wilds speaking, addressed to one client. `kind` keys the client's
 * audio cue and nothing else; `text` is a server-composed sentence. There
 * is no coordinate, no bearing payload, no creature id, and no number in
 * any field — where a direction matters it is spoken ("off to the
 * north"), which is the navigation ruling applied to danger.
 */
export interface ThreatMessage {
  /** sign | noticed | stalked | pressed | struck | evaded | braced |
   *  covered | drove | felled | challenge | miss — an open word set;
   *  unknown kinds simply render as text with no cue. */
  kind: string;
  text: string;
}

export interface SampleResultMessage {
  spawnId: string;
  name: string;
  ok: boolean;
  reason?: SampleFailReason;
  /** Units extracted (present when ok). */
  units?: number;
  /** The stack's new total after combining. */
  stackUnits?: number;
}

/**
 * WebSocket close code for "this account signed in elsewhere" — one live
 * session per user; the newer login wins. A client seeing this must NOT
 * auto-rejoin (it would kick the newer session back, forever).
 */
export const KICKED_ELSEWHERE_CODE = 4409;

/**
 * The wire's version. Bump it whenever a replicated schema's FIELD ORDER
 * changes — a field added, removed or reordered in any state class, in the
 * declaration, the constructor or the defineTypes call.
 *
 * Colyseus encodes fields by index, not by name, and a decoder built from
 * a different class list does not complain: it reads index 2 as whatever
 * index 2 means to IT. A client left open across a deploy that added a
 * field therefore decoded the new stream silently and wrongly — reading a
 * body's `world` as its `carriage` and rendering it — with no throw, no
 * warning and no handshake anywhere in the codebase to catch it. A wrong
 * answer delivered confidently is worse than a refused connection, and
 * "the game lying about physics" is the thing the-edge.md refuses by name.
 *
 * The join carries this and the door checks it, so a stale client is told
 * to reload rather than shown a world that is not there.
 */
export const PROTOCOL_VERSION = 1;

export interface ClubJoinOptions {
  /**
   * One-time WebSocket ticket from POST /api/ws-ticket. The game port never
   * sees credentials or cookies; the ticket maps to a user server-side and
   * dies on first use (or after a few seconds).
   */
  ticket: string;
  /**
   * The client's PROTOCOL_VERSION. Absent or different means the tab was
   * opened before a deploy that moved a schema field, and its decoder
   * would read this stream against the old field order.
   */
  protocolVersion?: number;
}
